/**
 * What a product page says about itself to search engines, in one place.
 *
 * Two readers need exactly the same answer: the page, through useSeo() when it
 * renders, and scripts/prerender.mjs, which writes a static HTML file per
 * product at build time so crawlers that do not run JavaScript -- AI search
 * crawlers, link previews in Discord and on social sites, and Google's own
 * first pass -- get the real title, description and structured data rather
 * than one generic "Veyra products" page shared by every product. If the two
 * were written separately they would drift, and a page whose static and
 * rendered heads disagree is worse than either alone.
 */
import type { Game } from "../types/games";
import { productFaq } from "../data/productFaq";
import { amount } from "../utils/plans";
import { reviewsFor } from "../data/reviews";
import { salesKey } from "../utils/salesKey";

/* The admin sales switches (settings.productSalesEnabled), when known. The
   prerender fetches them once per build and sets them here, so a title whose
   sales are switched off is not advertised to crawlers as in stock while the
   page itself says "Purchases paused". Unknown means status decides alone. */
let salesSwitches: Record<string, boolean> | null = null;
export function setSalesSwitches(map: Record<string, boolean> | null) {
  salesSwitches = map;
}
export const salesSwitchedOff = (game: Game) => salesSwitches?.[salesKey(game.id)] === false;
export const salesSwitchedOffKey = (key: string) => salesSwitches?.[key] === false;

export const SITE = "https://veyra.services";

/* The products page's title and description, shared with the prerender. */
/* Leads with the games on sale and the entry price: the old "All Cheats"
   title lost clicks in Search Console Insights, and Roblox is paused. */
export const PRODUCTS_TITLE = "Deadlock, CS2 & FiveM Cheats: Prices From €5/Week | Veyra";
export const PRODUCTS_DESCRIPTION =
  "Every Veyra cheat with live prices: Deadlock Internal + External, CS2 and FiveM. Aimbot, ESP and scripts from €5 a week, instant access, free Deadlock version on Discord.";

/** The canonical path of a product page. Replaces /product?game=<id>. */
export const productPath = (id: string) => `/product/${id}`;

const PLAN_NAMES: Record<string, string> = {
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  biannual: "Bi-Annual",
  lifetime: "Lifetime",
};

const isLive = (game: Game) =>
  game.status !== "coming-soon" && game.status !== "in-development" && game.status !== "partial-development";

/** "Deadlock External + Internal" -> "Deadlock". */
const gameWord = (game: Game) => game.name.split(" ")[0];

/* What each product page says in search results, written per game.

   The template these replace gave every product the same title shape
   ("CS2 External · Undetected CS2 Cheat") and the same description ("aimbot,
   ESP and configurable scripts") -- CS2 has no scripts, and FiveM's menu is
   mostly things that line does not mention. A title is the strongest on-page
   signal there is, so each now leads with the words people search and names
   what that build really has (see data/featureMatrix.ts for the evidence).

   Product pages say "Buy": they are where you purchase. The guides
   (src/guides) own the research searches -- "deadlock cheats", "cs2 cheats"
   -- so the two do not compete for the same query. */
const COPY: Record<string, { kicker: string; title: string; description: string }> = {
  "deadlock-external": {
    kicker: "Deadlock cheat & hack",
    /* Buying intent. "Deadlock cheats" is /deadlock-cheats' search; with
       both pages chasing it, Search Console had the guide around 11 and this
       page around 32, each holding the other down. */
    title: "Buy Deadlock Cheat: €5/Week, Monthly or Lifetime | Veyra",
    description:
      "Buy the Veyra Deadlock cheat: Internal + External with aimbot, ESP and hero scripts. €5 a week, €10 a month or €150 lifetime, card checkout, instant access.",
  },
  "cs2-external": {
    kicker: "CS2 cheat",
    title: "Buy CS2 Cheat — Aimbot, Triggerbot, ESP & Chams | Veyra",
    description:
      "External CS2 cheat with aimbot, autowall, recoil control, triggerbot, ESP, chams, radar and grenade prediction. Open beta, instant access after payment.",
  },
  "fivem-external": {
    kicker: "FiveM cheat & mod menu",
    title: "Buy FiveM Mod Menu — Aimbot, ESP & Teleports | Veyra",
    description:
      "FiveM mod menu with aimbot, silent aim, triggerbot, player and vehicle ESP, teleports, vehicle and weapon spawning, godmode and noclip. Instant access.",
  },
  "roblox-external": {
    kicker: "Roblox cheat",
    title: "Roblox Cheat — External Aimbot, Silent Aim & ESP | Veyra",
    description:
      "External Roblox cheat with aimbot, silent aim and ESP. It runs fully external and does not execute scripts, so there is no script hub. In beta.",
  },
  "apex-external": {
    kicker: "Apex Legends cheat",
    title: "Apex Legends Cheat — In Development | Veyra",
    description: "Veyra's Apex Legends cheat is in development and not for sale yet. Follow progress in the Veyra Discord.",
  },
  "rust-external": {
    kicker: "Rust cheat",
    title: "Rust Cheat — In Development | Veyra",
    description: "Veyra's Rust cheat is in development and not for sale yet. Follow progress in the Veyra Discord.",
  },
};

export function productTitle(game: Game) {
  return COPY[game.id]?.title ?? `${game.name} — ${gameWord(game)} Cheat | Veyra`;
}

export function productDescription(game: Game) {
  return COPY[game.id]?.description ?? game.description;
}

/** The small line above the product name in its heading: the search phrase,
    so the page's H1 says "CS2 cheat" and not only "CS2 External". */
export function productKicker(game: Game) {
  return COPY[game.id]?.kicker ?? `${gameWord(game)} cheat`;
}

/**
 * Structured data: the product and its offers, the questions answered on the
 * page, and where it sits in the site.
 *
 * `prices` are the live ones when the page has them, the bundled ones
 * otherwise; availability follows the same rule that decides whether the buy
 * button works, so a title still in development is not advertised as in
 * stock. Reviews are the Discord posts that name this product, shown on the
 * page as well. No rating and no aggregate: the posts are not star ratings, and
 * Google accepts a review without one when it has an author and a date.
 */
export function productJsonLd(game: Game, prices?: Record<string, string> | null, salesOpen?: boolean) {
  const url = `${SITE}${productPath(game.id)}`;
  const source = prices || (game.pricing as unknown as Record<string, string>);
  const live = isLive(game) && (salesOpen ?? !salesSwitchedOff(game));
  const offers = Object.entries(source)
    .filter(([plan]) => plan in PLAN_NAMES)
    .map(([plan, price]) => {
      const value = amount(String(price));
      if (!isFinite(value) || value <= 0) return null;
      return {
        "@type": "Offer",
        name: `${PLAN_NAMES[plan]} access`,
        price: value.toFixed(2),
        priceCurrency: "EUR",
        availability: live ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
        url,
      };
    })
    .filter(Boolean);

  const graph: Record<string, unknown>[] = [];
  /* Only for a product on sale. A title in development showed "Not for sale
     yet" on the page while its markup listed two priced offers, and markup
     has to agree with what the page says. */
  if (offers.length && live) {
    graph.push({
      "@type": "Product",
      name: game.name,
      /* The real description, not the tagline: "Our most established build"
         describes nothing a search engine can match. */
      description: game.description || productDescription(game),
      brand: { "@type": "Brand", name: "Veyra" },
      category: "GameApplication",
      ...(game.dropdownImage ? { image: `${SITE}${game.dropdownImage}` } : {}),
      offers,
      ...(reviewsFor(game.id).length
        ? {
            review: reviewsFor(game.id).map((r) => ({
              "@type": "Review",
              author: { "@type": "Person", name: r.author },
              datePublished: r.date,
              reviewBody: r.text,
            })),
          }
        : {}),
    });
  }
  /* The same questions the page renders; marking up an answer that is not on
     the page is what search engines penalise, so both come from one source. */
  graph.push({
    "@type": "FAQPage",
    mainEntity: productFaq(game.name).map((entry) => ({
      "@type": "Question",
      name: entry.question,
      acceptedAnswer: { "@type": "Answer", text: entry.answer },
    })),
  });
  graph.push({
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Products", item: `${SITE}/products` },
      { "@type": "ListItem", position: 3, name: game.name, item: url },
    ],
  });
  return { "@context": "https://schema.org", "@graph": graph };
}
